import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
} from '@mui/icons-material';
import LeaveRequestService, {
  LeaveRequest,
  LEAVE_TYPES,
} from '../../services/leave-request.service';
import EmployeeService from '../../services/employee.service';

interface ApprovalInfo {
  status: string;
  actionByName: string;
  actionDate: string;
  rejectionReason?: string;
}

const LeaveRequestForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [approvalInfo, setApprovalInfo] = useState<ApprovalInfo | null>(null);

  const [formData, setFormData] = useState<LeaveRequest>({
    employee: 0,
    leaveType: 'annual_leave',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    reason: '',
    status: 'pending',
    totalDays: 1,
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchLeaveRequest(parseInt(id));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const days = calculateTotalDays(formData.startDate, formData.endDate);
      setFormData((prev) => ({ ...prev, totalDays: days }));
    }
  }, [formData.startDate, formData.endDate]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'pagination[pageSize]': 100,
        'filters[status][$eq]': 'active',
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchLeaveRequest = async (leaveRequestId: number) => {
    try {
      setLoading(true);
      const response = await LeaveRequestService.getOne(leaveRequestId);
      const data = response.data?.data;

      if (data) {
        const attrs = data.attributes;
        setFormData({
          employee: attrs.employee?.data?.id || 0,
          leaveType: attrs.leaveType || 'annual_leave',
          startDate: attrs.startDate,
          endDate: attrs.endDate,
          reason: attrs.reason || '',
          status: attrs.status || 'pending',
          totalDays: attrs.totalDays || 1,
        });

        if (attrs.employee?.data) {
          setSelectedEmployee(attrs.employee.data);
        }

        if (attrs.status === 'approved') {
          const approver = attrs.approvedBy?.data?.attributes;
          setApprovalInfo({
            status: 'approved',
            actionByName: approver
              ? `${approver.firstName} ${approver.lastName}`
              : t('leave.unknownAdmin'),
            actionDate: attrs.approvalDate
              ? new Date(attrs.approvalDate).toLocaleString()
              : '',
          });
        } else if (attrs.status === 'rejected') {
          const rejector = attrs.rejectedBy?.data?.attributes;
          setApprovalInfo({
            status: 'rejected',
            actionByName: rejector
              ? `${rejector.firstName} ${rejector.lastName}`
              : t('leave.unknownAdmin'),
            actionDate: attrs.rejectionDate
              ? new Date(attrs.rejectionDate).toLocaleString()
              : '',
            rejectionReason: attrs.rejectionReason,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching leave request:', error);
      setError(t('leave.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 1;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEmployeeChange = (event: any, newValue: any) => {
    setSelectedEmployee(newValue);
    setFormData({ ...formData, employee: newValue?.id || 0 });
  };

  const validateForm = (): boolean => {
    if (!formData.employee) {
      setError(t('leave.selectEmployee'));
      return false;
    }
    if (!formData.startDate) {
      setError(t('leave.selectStartDate'));
      return false;
    }
    if (!formData.endDate) {
      setError(t('leave.selectEndDate'));
      return false;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      setError(t('leave.endDateAfterStart'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const submitData = {
        ...formData,
        totalDays: calculateTotalDays(formData.startDate, formData.endDate),
      };

      if (isEdit && id) {
        await LeaveRequestService.update(parseInt(id), submitData);
        setSuccess(t('leave.updateSuccess'));
      } else {
        await LeaveRequestService.create(submitData);
        setSuccess(t('leave.createSuccess'));
      }

      setTimeout(() => {
        navigate('/leave-requests');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving leave request:', error);
      setError(error.response?.data?.error?.message || t('leave.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const selectedLeaveType = LEAVE_TYPES.find((lt) => lt.value === formData.leaveType);

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEdit ? t('leave.editRequest') : t('leave.newRequest')}
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {approvalInfo && (
        <Alert
          severity={approvalInfo.status === 'approved' ? 'success' : 'error'}
          icon={approvalInfo.status === 'approved' ? <ApprovedIcon /> : <RejectedIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {approvalInfo.status === 'approved'
              ? t('leave.approvedByMessage', { name: approvalInfo.actionByName })
              : t('leave.rejectedByMessage', { name: approvalInfo.actionByName })}
          </Typography>
          {approvalInfo.actionDate && (
            <Typography variant="caption" display="block">
              {t('leave.actionDate')}: {approvalInfo.actionDate}
            </Typography>
          )}
          {approvalInfo.rejectionReason && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t('leave.rejectionReason')}: {approvalInfo.rejectionReason}
            </Typography>
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('leave.details')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Autocomplete
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                options={employees}
                loading={employeesLoading}
                disabled={isEdit && formData.status !== 'pending'}
                getOptionLabel={(option) =>
                  `${option.attributes?.firstName} ${option.attributes?.lastName} (${option.attributes?.employeeNumber})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t('leave.employee')} *`}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                select
                fullWidth
                label={t('leave.leaveType')}
                name="leaveType"
                value={formData.leaveType}
                onChange={handleChange}
                required
                disabled={isEdit && formData.status !== 'pending'}
                helperText={selectedLeaveType?.description || ''}
              >
                {LEAVE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>

              {selectedLeaveType && (
                <Chip
                  label={t(`leave.category.${selectedLeaveType.category}`)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t('leave.startDate')}
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  disabled={isEdit && formData.status !== 'pending'}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label={t('leave.endDate')}
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                  disabled={isEdit && formData.status !== 'pending'}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Stack>

              <Card variant="outlined" sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">{t('leave.totalDays')}</Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formData.totalDays} {formData.totalDays === 1 ? t('leave.day') : t('leave.daysUnit')}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              <TextField
                fullWidth
                label={t('leave.reason')}
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                multiline
                rows={4}
                placeholder={t('leave.reasonPlaceholder')}
                helperText={t('leave.reasonHelper')}
                disabled={isEdit && formData.status !== 'pending'}
              />

              {isEdit && (
                <TextField
                  select
                  fullWidth
                  label={t('leave.status')}
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="pending">{t('leave.pending')}</MenuItem>
                  <MenuItem value="approved">{t('leave.approved')}</MenuItem>
                  <MenuItem value="rejected">{t('leave.rejected')}</MenuItem>
                  <MenuItem value="cancelled">{t('leave.cancelled')}</MenuItem>
                </TextField>
              )}
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/leave-requests')}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || (isEdit && formData.status !== 'pending')}
              >
                {loading ? t('common.saving') : isEdit ? t('leave.updateRequest') : t('leave.submitRequest')}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default LeaveRequestForm;
